import config from 'config';
import { useEffect } from 'react';
import { hasValue } from 'swipeclock-frontend';

/**
 * React Hook for White Labeling
 * @param {Function} postWhiteLabelBrandingFn - Function to call the API
 * @param {string} whiteLabelClient - White label client hostname
 * @param {boolean} isEnclosedMode - Whether the app is in enclosed mode
 * @returns {Object} White label state and loading status
 */
export const useWhiteLabeling = (
  postWhiteLabelBrandingFn,
  whiteLabelClient,
  theme,
  setWhiteLabelState,
  isEnclosedMode = false
) => {
  useEffect(() => {
    const loadWhiteLabeling = async () => {
      try {
        const state = await applyWhiteLabeling(
          postWhiteLabelBrandingFn,
          whiteLabelClient,
          theme,
          isEnclosedMode
        );
        setWhiteLabelState({
          ...state,
          isLoading: false,
        });
      } catch (error) {
        console.error('Error loading white labeling:', error);
        setWhiteLabelState({
          isLoading: false,
        });
      }
    };

    loadWhiteLabeling();
  }, [whiteLabelClient, isEnclosedMode]);
};

/**
 * Fetches white label branding payload from the server
 * @param {Function} postWhiteLabelBrandingFn - Function to call the API
 * @param {string} whiteLabelClient - White label client hostname
 * @returns {Promise} Promise that resolves with branding data
 */
export const httpGetWhiteLabelPayload = async (
  postWhiteLabelBrandingFn,
  whiteLabelClient
) => {
  try {
    const result = await postWhiteLabelBrandingFn({
      whiteLabelClient,
    }).unwrap();
    return result;
  } catch (error) {
    console.error('Error fetching white label payload:', error);
    throw error;
  }
};

/**
 * Main white labeling application function
 * @param {Function} postWhiteLabelBrandingFn - Function to call the API
 * @param {string} whiteLabelClient - White label client hostname
 * @param {boolean} isEnclosedMode - Whether the app is in enclosed mode
 * @returns {Promise<Object>} Promise that resolves with white label state
 */
export const applyWhiteLabeling = async (
  postWhiteLabelBrandingFn,
  whiteLabelClient,
  theme,
  isEnclosedMode = false
) => {
  const whiteLabelState = {
    isEnclosedMode,
    hasWhiteLabelClient: false,
    urlLogo: null,
    labelProvidedBy: '',
    urlESS: null,
    backgroundColor: '',
    labelColor: '',
    noHeader: false,
  };

  if (whiteLabelClient) {
    whiteLabelState.hasWhiteLabelClient = true;

    try {
      const data = await httpGetWhiteLabelPayload(
        postWhiteLabelBrandingFn,
        whiteLabelClient
      );

      // Set logo and branding
      whiteLabelState.urlLogo = data.siteLogo || null;
      whiteLabelState.labelProvidedBy = data.siteAlias || '';
      whiteLabelState.backgroundColor = hasValue(data.siteColor1)
        ? `#${data.siteColor1}`
        : '';
      whiteLabelState.labelColor = hasValue(data.siteColor2)
        ? `#${data.siteColor2}`
        : '';

      // Update ESS URL if needed
      if (config.urlESS) {
        whiteLabelState.urlESS = config.urlESS.replace(
          /https?:\/\/[^/]*/,
          `https://${whiteLabelClient}`
        );
      }
    } catch (error) {
      console.error('Failed to apply white labeling:', error);
      throw new Error(error);
    }
  } else if (isEnclosedMode) {
    // Apply default enclosed mode styling
    whiteLabelState.backgroundColor = theme.palette.primary.contrastText;
    whiteLabelState.labelColor = theme.palette.custom.grey[800];
  }

  if (isEnclosedMode) {
    whiteLabelState.noHeader = true;
  }

  return whiteLabelState;
};
